/* eslint-disable max-lines */
import usePersistentState from '@gaia/hooks/usePersistentState';
import {GaiaAvatar, GaiaName} from '@gaia/icons';
import GaiaIcon from '@gaia/icons/GaiaIcon';
import {useUser} from '@gaia/stores/user';
import type {ImportState} from '@gaia/types';
import {idle as idleState} from '@gaia/types/ImportState';
import prefetchSavedItemsSidebar from '@gaia/utils/gaia/prefetchSavedItemsSidebar';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import AppButtons from '@map/AppButtons';
import classes from '@map/controls/Controls.module.css';
import {getMapCenter} from '@map/getMapCenter';
import SubscriptionTooltip from '@map/SubscriptionTooltip';
import {Divider, Drawer, IconButton, List, Typography} from '@mui/material';
import clsx from 'clsx';
import {createContext, memo, useCallback, useEffect} from 'react';

import {GaiaMapControl} from './GaiaMapControl';

export function getMapControlsWidth(controlsExpanded: boolean) {
    return controlsExpanded ? 140 : 40;
}

export const MapControlsExpandedContext = createContext(true);

interface ControlsDrawerProps {
    onSidebarChange: SetState<SidebarState>;
    sidebar: SidebarState;
    onExpand: VoidFunction;
    onCollapse: VoidFunction;
    setSelections: SetState<SelectionState[]>;
    showControls: Record<string, boolean>;
    interactionType: InteractionState['type'];
    is3D: boolean;
    setImportState: SetState<ImportState>;
    importState: ImportState;
    map: mapboxgl.Map | undefined;
    isEmbedded: boolean | undefined;
}

const ControlsDrawer = memo(function ControlsDrawer({
    onSidebarChange,
    sidebar,
    onExpand,
    onCollapse,
    interactionType,
    setImportState,
    importState,
    is3D,
    map,
    setSelections,
    showControls,
    isEmbedded,
}: ControlsDrawerProps) {
    const {isAuthenticated, hasMobileApp} = useUser();

    const [open, setOpen] = usePersistentState<boolean>('controlsDrawer__open', true, {
        transformLocalStorage: (localStorageVal) => (isEmbedded ? false : localStorageVal),
    });

    useEffect(() => {
        if (open) onExpand();
        else onCollapse();
    }, [onCollapse, onExpand, open]);

    const handleDrawerOpen = useCallback(() => {
        setOpen(true);
        onExpand();
        ampli.openControlsDrawer({category: 'map2'});
    }, [onExpand, setOpen]); // `setOpen` is stable

    const handleDrawerClose = useCallback(() => {
        setOpen(false);
        onCollapse();
        ampli.closeControlsDrawer({category: 'map2'});
    }, [onCollapse, setOpen]); // `setOpen` is stable

    const disableDrawControls = Boolean(
        interactionType === 'editingWaypoint' ||
            interactionType === 'editingRoute' ||
            interactionType === 'editingArea' ||
            sidebar.type === 'customSource' ||
            (importState.status !== 'idle' && importState.status !== 'sidebarOpen')
    );

    const onImportClick = useCallback(() => {
        if (sidebar.type !== 'savedItems') {
            // If an import is not in progress create import state
            if (importState.status === 'idle') {
                setImportState({status: 'sidebarOpen'});
                ampli.openSidebar({type: 'import'});
            }

            onSidebarChange({type: 'savedItems'});
        } else if (importState.status !== 'idle' && importState.status === 'sidebarOpen') {
            // Clear the import state if the sidebar is open but an import is not in progress
            setImportState(idleState);
            onSidebarChange({type: 'hidden'});
            ampli.closeSidebar({type: 'import'});
        } else if (importState.status === 'idle' && sidebar.type === 'savedItems') {
            setImportState({status: 'sidebarOpen'});
            ampli.openSidebar({type: 'import'});
        } else {
            // Don't clear the import state if an import is in progress
            ampli.closeSidebar({type: 'import'});
            onSidebarChange({type: 'hidden'});
        }
    }, [sidebar.type, importState, onSidebarChange, setImportState]);

    const showImportControl = window.location.pathname === '/map/';

    const isLayerSidebar =
        sidebar.type === 'layers' ||
        sidebar.type === 'layerDetails' ||
        sidebar.type === 'editLayers';

    const onSidebarChangeAnalytics = useCallback(
        (nextSidebar: SidebarState) => {
            if (nextSidebar.type === 'hidden') {
                ampli.closeSidebar({type: sidebar.type});
            } else {
                if (importState.status === 'sidebarOpen') setImportState(idleState);
                ampli.openSidebar({type: sidebar.type});
            }
            onSidebarChange(nextSidebar);
        },
        [importState, onSidebarChange, setImportState, sidebar.type]
    );

    const toggleSidebar = useCallback(
        (nextSidebar: SidebarState) => {
            // If the import sidebar is open the sidebar.type is already savedItems, so in order to
            // close the import sidebar and open the saved items we just toggle the importState
            if (
                importState.status !== 'idle' &&
                sidebar.type === 'savedItems' &&
                nextSidebar.type === 'savedItems'
            ) {
                setImportState(idleState);
            } else if (sidebar.type !== nextSidebar.type) {
                onSidebarChangeAnalytics(nextSidebar);
            } else {
                onSidebarChangeAnalytics({type: 'hidden'});
            }
        },
        [importState, onSidebarChangeAnalytics, setImportState, sidebar.type]
    );

    const onLayersControlClick = useCallback(() => {
        if (isLayerSidebar) {
            onSidebarChangeAnalytics({type: 'hidden'});
        } else {
            onSidebarChangeAnalytics({type: 'layers'});
        }
    }, [isLayerSidebar, onSidebarChangeAnalytics]);

    const onSavedItemsClick = useCallback(() => {
        toggleSidebar({type: 'savedItems'});
    }, [toggleSidebar]);

    const prefetchSavedItems = useCallback(
        () => prefetchSavedItemsSidebar(isAuthenticated),
        [isAuthenticated]
    );

    const onPreferencesClick = useCallback(() => {
        toggleSidebar({type: 'preferences'});
    }, [toggleSidebar]);

    const onPrintClick = useCallback(() => {
        toggleSidebar({type: 'print'});
    }, [toggleSidebar]);

    const isEditing =
        interactionType === 'editingWaypoint' ||
        interactionType === 'editingRoute' ||
        interactionType === 'editingArea';

    if (Object.values(showControls).every((v) => v === false)) return null;

    return (
        <MapControlsExpandedContext.Provider value={open}>
            <Drawer
                variant='permanent'
                classes={{
                    root: classes.drawer,
                    paper: classes.drawerPaper,
                }}
            >
                <div className={classes.gaiaHeader}>
                    <GaiaAvatar style={{width: 26, height: 24, flexShrink: 0}} />
                    <GaiaName />
                </div>

                <Divider />
                <Typography
                    component='h1'
                    className={clsx(classes.listSubheader, classes.listTransform)}
                >
                    Map
                </Typography>
                <List style={{paddingTop: 0}}>
                    {showControls.overlays && (
                        <GaiaMapControl
                            active={sidebar.type === 'overlays'}
                            disabled={sidebar.type === 'customSource'}
                            icon={'overlays'}
                            label={'Overlays'}
                            title={'View Map Overlays'}
                            onClick={() => {
                                toggleSidebar({
                                    type: 'overlays',
                                    prevSidebar: sidebar,
                                });
                            }}
                        />
                    )}
                    {showControls.layer && (
                        <GaiaMapControl
                            active={isLayerSidebar}
                            disabled={sidebar.type === 'customSource'}
                            icon={'layers'}
                            label={'Layers'}
                            title={'View Map Layers'}
                            onClick={onLayersControlClick}
                        />
                    )}
                    {showControls.savedItems && (
                        <GaiaMapControl
                            active={sidebar.type === 'savedItems' && importState.status === 'idle'}
                            disabled={
                                isEditing ||
                                sidebar.type === 'customSource' ||
                                (importState.status !== 'idle' &&
                                    importState.status !== 'sidebarOpen')
                            }
                            icon={'folder'}
                            title={'Manage saved waypoints, routes, tracks, areas, and folders'}
                            label={'Saved Items'}
                            onClick={isAuthenticated ? onSavedItemsClick : undefined}
                            onMouseEnter={prefetchSavedItems}
                            analyticsContext={'saved items control'}
                            subscription={'user'}
                        />
                    )}
                    {showControls.preferences && (
                        <GaiaMapControl
                            active={sidebar.type === 'preferences'}
                            disabled={isEditing || sidebar.type === 'customSource'}
                            icon={'settings'}
                            label={'Preferences'}
                            title={'Change Map Preferences'}
                            onClick={onPreferencesClick}
                        />
                    )}
                </List>

                <Divider />

                <Typography
                    component='h1'
                    className={clsx(classes.listSubheader, classes.listTransform)}
                >
                    Create
                </Typography>
                <List style={{paddingTop: 0}}>
                    {showControls.draw && (
                        <>
                            <GaiaMapControl
                                active={interactionType === 'editingWaypoint'}
                                disabled={disableDrawControls}
                                title={'Create Waypoint'}
                                icon={'waypoint'}
                                label={'Waypoint'}
                                onClick={() => {
                                    if (interactionType === 'editingWaypoint') {
                                        onSidebarChange({type: 'editWaypoint'});
                                    } else {
                                        setSelections([]);

                                        const newWaypointCoordinate = getMapCenter(
                                            map,
                                            open,
                                            sidebar
                                        );

                                        if (!newWaypointCoordinate) {
                                            return;
                                        }

                                        onSidebarChange({
                                            type: 'editWaypoint',
                                            id: '',
                                            initialWaypoint: {
                                                type: 'Feature',
                                                properties: {},
                                                geometry: {
                                                    type: 'Point',
                                                    coordinates: newWaypointCoordinate,
                                                },
                                            },
                                        });

                                        ampli.markWaypoint({
                                            category: 'map2',
                                            location: 'Map controls drawer',
                                        });

                                        setImportState(idleState);
                                    }
                                }}
                            />
                            <GaiaMapControl
                                active={interactionType === 'editingRoute'}
                                disabled={disableDrawControls}
                                icon={'route'}
                                label={'Route'}
                                title={'Create Route'}
                                onClick={() => {
                                    if (!map) return;
                                    setSelections([]);
                                    onSidebarChange({type: 'editTrack'});
                                    ampli.createRoute({location: 'controls-drawer'});
                                    setImportState(idleState);
                                }}
                            />
                            <GaiaMapControl
                                active={interactionType === 'editingArea'}
                                disabled={disableDrawControls}
                                icon={'area'}
                                label='Area'
                                title={'Create Area'}
                                onClick={() => {
                                    setSelections([]);
                                    setImportState(idleState);
                                    onSidebarChange({type: 'editArea', id: ''});
                                }}
                            />
                            {showImportControl && (
                                <GaiaMapControl
                                    active={importState.status !== 'idle'}
                                    disabled={disableDrawControls}
                                    icon={'import'}
                                    title={'Import Data'}
                                    label={'Import Data'}
                                    onClick={onImportClick}
                                />
                            )}
                        </>
                    )}
                    {showControls.print && (
                        <GaiaMapControl
                            active={sidebar.type === 'print'}
                            disabled={
                                isEditing ||
                                sidebar.type === 'customSource' ||
                                is3D ||
                                (importState.status !== 'idle' &&
                                    importState.status !== 'sidebarOpen')
                            }
                            icon={'print'}
                            label={'Print Map'}
                            title={'Print'}
                            onClick={onPrintClick}
                        />
                    )}
                </List>

                <Divider />

                {isAuthenticated && !hasMobileApp && (
                    <div className={clsx(classes.appButtons, classes.listTransform)}>
                        <Typography component='h1' className={classes.listSubheader}>
                            Go Mobile
                        </Typography>
                        <AppButtons analyticsContext='controls_drawer' />
                    </div>
                )}

                <div
                    style={{
                        marginTop: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Divider />

                    <List style={{padding: 0}}>
                        <GaiaMapControl
                            active={false}
                            disabled={false}
                            icon={'help'}
                            label={'Help'}
                            title={'Help'}
                            onClick={() => {
                                window.open('https://help.gaiagps.com/', '_blank');
                            }}
                        />
                    </List>

                    <Divider />

                    <SubscriptionTooltip
                        placement='right'
                        title={open ? 'Collapse Controls' : 'Expand Controls'}
                        enterDelay={500}
                        subscription={undefined}
                    >
                        <IconButton
                            aria-label={open ? 'Collapse Controls' : 'Expand Controls'}
                            onClick={open ? handleDrawerClose : handleDrawerOpen}
                            className={classes.drawerToggle}
                            size='large'
                        >
                            <GaiaIcon name='collapse' />
                        </IconButton>
                    </SubscriptionTooltip>
                </div>
            </Drawer>
        </MapControlsExpandedContext.Provider>
    );
});

export default ControlsDrawer;
